import { Vector3 } from 'three';
import { ITexture } from '../../@types/ContextProviderRelated';
import { EAssetType } from '../../@types/FilesAPIModels';
import ProductModel from '../../models/standard/ProductModel';
import filesApiService from '../filesApiService';
import { parseFilesApiSingleProductRequest } from '../jsonParser';
import { NonFetchableEntity } from '../planJsonExportImportUtils';
import { createAttributes, createObjects } from '../transformation/tranformToMultiRoomBuilding';
import { fetchTextureFromApi } from './fetchTextureFromApi';

export type FetchProductResult =
  | ProductModel
  | NonFetchableEntity
  | {
      type: EAssetType | undefined;
      entityData: any;
      product: ProductModel;
      textureFetchingSuccesful: boolean;
      textureId?: string;
    };

    export async function fetchProductById(
      id: number | string,
      position: Vector3,
      rotation: number,
      rawProductData: any,
      textureData?: any
    ): Promise<FetchProductResult> {
      try {
        const response = await filesApiService.dataRequest(`product/${id}`);
        const productData = parseFilesApiSingleProductRequest(response);
    
        const modelAssets = productData.assets.filter((asset: any) => asset.assetType === EAssetType.MODEL);
    
        if (modelAssets.length === 0) {
          return {
            objectType: 'notFetchableEntity',
            modelType: 'product',
            type: 'assets',
            id: String(id),
            subType: undefined,
            entityData: rawProductData,
          };
        }
    
        const firstAsset = modelAssets[0];
        const modelData = await filesApiService.getProductModelById(productData.id, firstAsset.id);
    
        if (!modelData) {
          return {
            objectType: 'notFetchableEntity',
            modelType: 'product',
            type: 'modelData',
            id: String(id),
            subType: undefined,
            entityData: rawProductData,
          };
        }
    
        // --- TEXTURE (unchanged) ---
        let textureInstantiationObject: Awaited<ReturnType<typeof fetchTextureFromApi>> | null = null;
        let textureFetchingSuccesful = true;
    
        if (textureData) {
          textureInstantiationObject = await fetchTextureFromApi(textureData);
          textureFetchingSuccesful = (textureInstantiationObject?.fetchingSuccesful || !textureInstantiationObject.neededToFetchTexture) ?? false;
        }
    
        // --- NEW: only take dimensions + svg from Files API, keep meta & attributes from rawProductData ---
    
        const base = rawProductData;
    
        const modelLength = modelData.length ?? base.dimensions?.length ?? 0;
        const modelDepth  = modelData.depth  ?? base.dimensions?.depth  ?? 0;
    
        const svgPath = await filesApiService.getSVGFileFromProduct(productData.id, firstAsset.id);
    
        // Rebuild attributes/objects from existing serialized data (NOT from Files API)
        const attributes = createAttributes(base.attributes);
        const objects    = createObjects(base.objects);
    
        const product = new ProductModel(
          String(base.id ?? productData.id),
          base.name ?? productData.name, // prefer existing name
          svgPath || base.svgPath,
          modelLength,
          base.dimensions?.height ?? 0,
          modelDepth,
          position,
          rotation,
          base.fillColor,
          base.strokeColor,
          base.strokeWidth ?? 1.5,
          textureInstantiationObject?.texture as ITexture | undefined,
          attributes,
          objects,
          base.description ?? productData.description,
          base.manufacturer ?? productData.manufacturer,
          undefined,
          base.country ?? productData.country
        );
    
        if (base.uuid) {
          product.setUUID(base.uuid);
        }
    
        if (textureData && !textureFetchingSuccesful) {
          return {
            type: EAssetType.MATERIAL,
            entityData: rawProductData,
            product: product,
            textureFetchingSuccesful: false,
            textureId: textureData.id,
          };
        }
    
        return product;
      } catch (error) {
        console.error('Error fetching product by id', id, error);
        return {
          objectType: 'notFetchableEntity',
          modelType: 'product',
          type: 'unknown',
          id: String(id),
          subType: undefined,
          entityData: rawProductData,
        };
      }
    }
    